import React, { Fragment, useState, useRef, useEffect } from "react";
import BigCalendar from "../../../components/BigCalendar/BigCalendar";
import Select from "react-select";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { DropdownIndicator } from "../../../utils/commonSelectBoxOptions/commonSelectBoxOptions";
import moment from "moment";
import { generateDates } from "../../../utils/FormatDate/generateDates";

const HolidayCalendar = () => {
  const axiosPrivate = useAxiosPrivate();
  const [holidayList, setHolidayList] = useState(null);
  const [eventList, setEventList] = useState([]);
  const [locationList, setLocationList] = useState({
    country: [],
    state: [],
    district: [],
  });
  const [location, setLocation] = useState({
    country: 0,
    state: 0,
    city: 0,
    date: "",
    search: "",
  });

  const [dateRange, setDateRange] = useState({
    fromDate: moment().startOf("month").subtract(7, "days").format("YYYY-MM-DD"),
    toDate: moment().endOf("month").add(7, "days").format("YYYY-MM-DD"),
  });

  const { fromDate, toDate } = dateRange;
  const [pageNo, setPageNo] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [makeApiCall, setMakeApiCall] = useState(false);

  const filterRef = useRef({
    country: "",
    state: "",
    date: "",
  });

  const isFilterApplied =
    location.country || location.state || location.date || location.search;

  const handleClearFilters = () => {
    setLocation({
      country: 0,
      state: 0,
      city: 0,
      date: "",
    });
    filterRef.current.country?.clearValue();
    filterRef.current.state?.clearValue();
  };

  const fetchLocationList = async (baseUrl) => {
    await axiosPrivate.get(baseUrl).then((response) => {
      const res = response?.data;
      if (res?.status || res?.statusCode === 200) {
        setLocationList((prev) => ({
          ...prev,
          country: res.country || prev.country,
          state: res.state || prev.state,
          city: res.city || prev.city,
        }));
      }
    });
  };

  const fetchHolidayList = async (url) => {
    await axiosPrivate
      .get(url)
      .then((response) => {
        const res = response?.data;
        if (res?.status) {
          setTotalRecords(res.total);
          setHolidayList(res.holidays);
        } else {
          setHolidayList(null);
        }
      })
      .catch(() => setHolidayList(null));
  };

  useEffect(() => {
    let baseUrl = `onboarding/services/location?`;
    let holidayUrl = `lms/holiday/holidayList?holiday&page_number=${pageNo}`;

    if (location.country) {
      baseUrl += `StatesFrom=${location.country}`;
      holidayUrl += `&country_id=${location.country}`;
    }
    if (location.state) {
      baseUrl += `&CityFrom=${location.state}`;
      holidayUrl += `&state_id=${location.state}`;
    }
    if (fromDate) holidayUrl += `&fromDate=${fromDate}`;
    if (toDate) holidayUrl += `&toDate=${toDate}`;

    fetchLocationList(baseUrl);
    fetchHolidayList(holidayUrl);
  }, [location, pageNo, makeApiCall, dateRange]);

  const handleChangeDate = ({ start, end }) => {
    if (start && end) {
      setDateRange({
        fromDate: moment(start).format("YYYY-MM-DD"),
        toDate: moment(end).format("YYYY-MM-DD"),
      });
    }
  };

  useEffect(() => {
    const formattedData = [];

    if (Array.isArray(holidayList)) {
      const dates = generateDates(fromDate, toDate);

      dates.forEach((date) => {
        const holiday = holidayList.find(
          (h) => moment(h.date).format("YYYY-MM-DD") === date
        );

        if (holiday) {
          const {
            holiday_name,
            holiday_type_name,
            holiday_type_id,
            is_optional,
            date: holidayDate,
          } = holiday;

          let color = "#C072EA"; // Regional Holiday
          if (is_optional === true) {
            color = "#FDBA74"; // Optional Holiday
          } else if (Number(holiday_type_id) === 1) {
            color = "#95ED9E"; // Public Holiday
          }

          formattedData.push({
            title: (
              <div>
                <p>{holiday_name}</p>
                <p className="text-xs">
                  {holiday_type_name}
                  {is_optional && (
                    <span className="ml-1 text-orange-600">(Optional)</span>
                  )}
                </p>
              </div>
            ),
            start: new Date(moment(holidayDate).format("YYYY-MM-DD")),
            end: new Date(moment(holidayDate).format("YYYY-MM-DD")),
            backGroundColor: color,
            borderColor: "#021704",
          });
        }
      });
    }

    setEventList(formattedData);
  }, [holidayList, fromDate, toDate]);

  return (
    <Fragment>
      <div className="bg-gray-50 flex-1 px-10 pt-8 pb-6">
        {/* Header */}
        <div className="mb-3">
          <h2 className="text-lg font-semibold">Holiday Calendar</h2>
          <p className="text-sm text-gray-600">
            Employees can avail <span className="font-medium">only one optional holiday</span> per year.
          </p>
        </div>

        {/* Color */}
        <div className="flex gap-6 text-sm text-gray-700 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-400"></span>
            Public Holiday
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-400"></span>
            Optional Holiday
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-purple-400"></span>
            Regional Holiday
          </div>
        </div>

        {/* Filters */}
        <div className="flex justify-between items-center px-10 mb-4">
          <div className="flex gap-3">
            <Select
              ref={(el) => (filterRef.current.country = el)}
              components={{ DropdownIndicator }}
              className="w-[200px]"
              placeholder="Country"
              options={locationList.country.map((c) => ({
                label: c.flag,
                value: c.id,
              }))}
              onChange={(e) =>
                setLocation((prev) => ({ ...prev, country: e?.value }))
              }
            />

            <Select
              ref={(el) => (filterRef.current.state = el)}
              components={{ DropdownIndicator }}
              className="w-[200px]"
              placeholder="State"
              options={locationList.state.map((s) => ({
                label: s.state_name,
                value: s.id,
              }))}
              onChange={(e) =>
                setLocation((prev) => ({ ...prev, state: e?.value }))
              }
            />

            {isFilterApplied && (
              <button
                className="text-blue-600 font-medium"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Calendar */}
        <BigCalendar
          myEventsList={eventList}
          handleChangeDate={handleChangeDate}
        />
      </div>
    </Fragment>
  );
};

export default HolidayCalendar;
